import type { CopilotCatchUpOptionStore } from '../store/store';
import type { MailboxInfo } from 'owa-client-types';
import { getIndexerValueForMailboxInfo } from 'owa-client-types';
import { getItem, setItem, itemExists, type LocalStorageKeys } from 'owa-local-storage';
import { isFeatureEnabled } from 'owa-feature-flags';
import { logUsage } from 'owa-analytics';
import { getCopilotTopics } from 'owa-mail-copilot-settings/lib/selectors/getCopilotTopics';

const DEFAULT_TOPICS_PROMPT = `Search using the query '{topicTitle} or messages that match the following description: {topicDescription}'. If the query is a question, try to answer it using the search results. If it is not a question, gather the most relevant internal and external insights across meetings, emails, chats, files, and the web and give a comprehensive overview.`;

/**
 * Get the copilot catchup settings for the mailbox
 * @param mailboxInfo the mailbox info for current user
 */
export function getCopilotCatchUpOptionStore(mailboxInfo: MailboxInfo): CopilotCatchUpOptionStore {
    const key = getIndexerValueForMailboxInfo(mailboxInfo);
    const localStoragePrompt = fetchCopilotPromptFromLocalStorage(key);
    return {
        id: localStoragePrompt?.id ?? key,
        isEnabled: localStoragePrompt?.isEnabled ?? isFeatureEnabled('mon-copilot-digest'), // Default to feature flight
        shouldUsePrompt: localStoragePrompt?.shouldUsePrompt ?? true, // Setting to true by default to ease testing
        shouldApplyPromptToIncomingMessages:
            localStoragePrompt?.shouldApplyPromptToIncomingMessages ?? false,
        includeAllEmail: localStoragePrompt?.includeAllEmail ?? true,
        timeFrame: localStoragePrompt?.timeFrame ?? '3days',
        needsReplyItemIds: localStoragePrompt?.needsReplyItemIds ?? [],
        needsReplyConversationIds: localStoragePrompt?.needsReplyConversationIds ?? [],
        needsActionItemIds: localStoragePrompt?.needsActionItemIds ?? [],
        needsActionConversationIds: localStoragePrompt?.needsActionConversationIds ?? [],
        needsScheduleItemIds: localStoragePrompt?.needsScheduleItemIds ?? [],
        needsScheduleConversationIds: localStoragePrompt?.needsScheduleConversationIds ?? [],
        extractedTaskDetails: localStoragePrompt?.extractedTaskDetails ?? {},
        ocsTopics: JSON.stringify(JSON.parse(getCopilotTopics(mailboxInfo)), undefined, 2),
        topicsBizchatPrompt: localStoragePrompt?.topicsBizchatPrompt ?? DEFAULT_TOPICS_PROMPT,
        forceResetItems: localStoragePrompt?.forceResetItems ?? true, // Default to true to reset items on first run
        useNaturalLanguageSummary: localStoragePrompt?.useNaturalLanguageSummary ?? false,
    };
}

/**
 * Get the copilot catchup settings from local storage
 * @param key the key to fetch the prompt
 */
function fetchCopilotPromptFromLocalStorage(key: string): CopilotCatchUpOptionStore | undefined {
    const localStorageKey: LocalStorageKeys = `mail_copilot_catchup_${key}`;
    try {
        return itemExists(window, localStorageKey)
            ? JSON.parse(getItem(window, localStorageKey) as string)
            : undefined;
    } catch (e) {
        return undefined;
    }
}

/**
 * Save the copilot prompt to local storage
 * @param copilotCatchUpSettings the copilot catchup object to save
 */
export const saveCopilotCatchUp = (copilotCatchUpSettings: CopilotCatchUpOptionStore): void => {
    const localStorageKey: LocalStorageKeys = `mail_copilot_catchup_${copilotCatchUpSettings.id}`;
    setItem(window, localStorageKey, JSON.stringify(copilotCatchUpSettings));

    logUsage('CopilotCatchUpSettingsSaved', {
        isEnabled: copilotCatchUpSettings.isEnabled,
        shouldUsePrompt: copilotCatchUpSettings.shouldUsePrompt,
        shouldApplyPromptToIncomingMessages:
            copilotCatchUpSettings.shouldApplyPromptToIncomingMessages,
        includeAllEmail: copilotCatchUpSettings.includeAllEmail,
        timeFrame: copilotCatchUpSettings.timeFrame,
        useNaturalLanguageSummary: copilotCatchUpSettings.useNaturalLanguageSummary,
    });
    logUsage('CopilotCatchUpApplyPromptToIncommingMessages', {
        shouldApplyPromptToIncomingMessages:
            copilotCatchUpSettings.shouldApplyPromptToIncomingMessages,
    });
};
